import { useEffect, useRef, useState } from 'react';
import Login from './pages/Login.tsx';
import Accueil, { type Periode } from './pages/Accueil.tsx';
import Saisie from './pages/Saisie.tsx';
import Preinventaire from './pages/Preinventaire.tsx';
import Inventaire from './pages/Inventaire.tsx';
import Rma from './pages/Rma.tsx';
import TableauSuivi from './pages/TableauSuivi.tsx';
import Historique from './pages/Historique.tsx';
import Export from './pages/Export.tsx';
import Parametres from './pages/Parametres.tsx';
import Synchronisation from './pages/Synchronisation.tsx';
import Abonnement from './pages/Abonnement.tsx';
import PaiementsAdmin from './pages/PaiementsAdmin.tsx';
import AdminComptes from './pages/AdminComptes.tsx';
import Plus from './pages/Plus.tsx';
import RapportsHebdo from './pages/RapportsHebdo.tsx';
import Commandes from './pages/Commandes.tsx';
import Referentiels from './pages/Referentiels.tsx';
import Validation from './pages/Validation.tsx';
import AdminAcces from './pages/AdminAcces.tsx';
import AdminAbonnements from './pages/AdminAbonnements.tsx';
import ChangerMdp from './pages/ChangerMdp.tsx';
import { chargerPermissions, moduleAutorise, profilCompte } from './config/permissions.ts';
import BandeauAbonnement from './components/BandeauAbonnement.tsx';
import { PorteModule } from './components/Verrou.tsx';
import { AbonnementProvider } from './hooks/useAbonnement.tsx';
import SyncBadge from './components/SyncBadge.tsx';
import { clearSession, getSession } from './api/client.ts';
import { effacerDonneesLocales, synchroniser } from './offline/dbModules.ts';
import { traiterFile } from './offline/syncQueue.ts';
import { effacerAbonnementLocal } from './offline/db.ts';
import { oublierPeriodes, periodeConnue } from './offline/periodesCache.ts';
import { Onglets, nomMois } from './components/ui.tsx';
import { NOM_APPLICATION } from './config/affichage.ts';
import Icone from './components/Icones.tsx';

type Page =
  | 'ACCUEIL' | 'SAISIE' | 'PREINV' | 'INV' | 'TABLEAUX' | 'EXPORT' | 'PLUS'
  | 'HISTORIQUE' | 'PARAMETRES' | 'SYNCHRO' | 'ABONNEMENT' | 'PAIEMENTS' | 'ADMIN_COMPTES' | 'RAPPORTS' | 'COMMANDES' | 'REFERENTIELS' | 'VALIDATION' | 'ADMIN_ACCES' | 'ADMIN_ABONNEMENTS';

// Menu principal (6 entrées + « Plus ») : simple, identique sur téléphone (barre du bas) et sur ordinateur (colonne de gauche).
// `pages` = écrans qui appartiennent à l'entrée (pour la mettre en surbrillance) ; `court` = libellé de la barre du bas sur téléphone.
const NAV: Array<{ page: Page; label: string; court: string; icone: string; pages: Page[]; modules?: string[] }> = [
  { page: 'ACCUEIL', label: 'Accueil', court: 'Accueil', icone: 'home', pages: ['ACCUEIL'] },
  { page: 'SAISIE', label: 'Saisie', court: 'Saisie', icone: 'saisie', pages: ['SAISIE'], modules: ['CONSULTATION', 'CPN', 'PEV', 'INTRANTS', 'FINANCE', 'DECES', 'COMMUNICATION'] },
  { page: 'PREINV', label: 'Pré-inventaire / Inventaire', court: 'Inventaire', icone: 'inv', pages: ['PREINV', 'INV'], modules: ['PREINVENTAIRE', 'INVENTAIRE'] },
  { page: 'RAPPORTS', label: 'Rapports journaliers et hebdomadaires (RSH et DHIS2)', court: 'Rapports', icone: 'rap', pages: ['RAPPORTS'], modules: ['RAPPORTS'] },
  { page: 'TABLEAUX', label: 'RMA (tableaux et exports)', court: 'RMA', icone: 'rma', pages: ['TABLEAUX', 'EXPORT'], modules: ['RMA'] },
  { page: 'COMMANDES', label: 'Bon de commande', court: 'Commande', icone: 'cmd', pages: ['COMMANDES'], modules: ['COMMANDES'] },
];
// « Plus » : écrans d'usage occasionnel et d'administration (rien n'est supprimé : tout ce qui existait reste accessible).
const PLUS: Array<{ page: Page; label: string; aide: string; rolesRequis?: string[] }> = [
  { page: 'HISTORIQUE', label: 'Historique', aide: 'Mois précédents' },
  { page: 'SYNCHRO', label: 'Synchronisation', aide: 'Saisies en attente, conflits' },
  { page: 'PARAMETRES', label: 'Paramètres', aide: 'Compte et préférences' },
  { page: 'ABONNEMENT', label: 'Mon abonnement', aide: 'Paiement et accès', rolesRequis: ['RESPONSABLE_CSB', 'AGENT_SAISIE'] },
  { page: 'PAIEMENTS', label: 'Paiements à valider', aide: 'Administration', rolesRequis: ['ADMIN'] },
  { page: 'ADMIN_COMPTES', label: 'Gestion des comptes', aide: 'Administration', rolesRequis: ['ADMIN'] },
  { page: 'VALIDATION', label: 'Saisies à valider', aide: 'Validation des saisies Stock / Agent', rolesRequis: ['RESPONSABLE_CSB', 'ADMIN'] },
  { page: 'ADMIN_ACCES', label: 'Accès Stock / Agent', aide: 'Modules, identifiants, mots de passe', rolesRequis: ['ADMIN'] },
  { page: 'ADMIN_ABONNEMENTS', label: 'Abonnements', aide: 'Packs, dates, statuts, activité', rolesRequis: ['ADMIN'] },
  { page: 'REFERENTIELS', label: 'Référentiels', aide: 'Codes RSH, produits de commande, DHIS2', rolesRequis: ['ADMIN'] },
];
const DERNIERE = 'prerma_derniere_periode';

export default function App() {
  const [connecte, setConnecte] = useState(!!getSession().token);
  const [mdpAChanger, setMdpAChanger] = useState(() => localStorage.getItem('prerma_mdp_a_changer') === '1');
  const [, majPermissions] = useState(0);
  useEffect(() => {
    const surMdp = () => setMdpAChanger(true);
    const surPerm = () => majPermissions((n) => n + 1);
    window.addEventListener('prerma:mdp-a-changer', surMdp);
    window.addEventListener('prerma:permissions', surPerm);
    if (getSession().token && localStorage.getItem('prerma_mdp_a_changer') !== '1') void chargerPermissions();
    return () => { window.removeEventListener('prerma:mdp-a-changer', surMdp); window.removeEventListener('prerma:permissions', surPerm); };
  }, [connecte]);
  const [page, setPage] = useState<Page>('ACCUEIL');
  const [sousTableaux, setSousTableaux] = useState<'RMA' | 'SUIVI' | 'EXPORT'>('RMA');
  const [periode, setPeriodeBrut] = useState<Periode | null>(() => {
    try { const d = JSON.parse(localStorage.getItem(DERNIERE) ?? 'null'); return d ? periodeConnue(d.annee, d.mois) : null; } catch { return null; }
  });

  function setPeriode(p: Periode | null) {
    setPeriodeBrut(p);
    if (p) localStorage.setItem(DERNIERE, JSON.stringify({ annee: p.annee, mois: p.mois })); else localStorage.removeItem(DERNIERE);
  }
  useEffect(() => { window.scrollTo(0, 0); }, [page]);
  /** Change d'écran ; « EXPORT » (ancien écran séparé) est maintenant l'onglet « Exports » de la page RMA. */
  function aller(p: Page) { if (p === 'EXPORT') { setSousTableaux('EXPORT'); setPage('TABLEAUX'); } else setPage(p); }

  // Session expirée (jeton de 8 h dépassé, typiquement après une journée hors connexion) : les saisies restent sur l'appareil ;
  // on propose de se reconnecter par-dessus l'écran en cours (rien n'est démonté, aucune saisie en cours n'est perdue).
  const [sessionExpiree, setSessionExpiree] = useState(false);
  const [reconnexion, setReconnexion] = useState(false);
  // Identité (rôle + CSB) du compte dont la session a expiré : sert à détecter qu'un AUTRE compte (ex. Admin) se reconnecte.
  const identiteAvant = useRef('');
  const identite = () => { const s = getSession(); return `${s.role ?? ''}|${s.csbId ?? ''}`; };
  useEffect(() => {
    const surExpiration = () => setSessionExpiree(true);
    window.addEventListener('prerma:session-expiree', surExpiration);
    return () => window.removeEventListener('prerma:session-expiree', surExpiration);
  }, []);

  if (!connecte) return <Login onLoggedIn={() => { setMdpAChanger(localStorage.getItem('prerma_mdp_a_changer') === '1'); setConnecte(true); }} />;
  if (mdpAChanger) return <ChangerMdp onFait={() => setMdpAChanger(false)} onSortie={() => { clearSession(); setMdpAChanger(false); setConnecte(false); }} />;

  const role = getSession().role;
  const nomCompte = getSession().nomCompte ?? (role === 'ADMIN' ? 'ADMIN' : null);
  const liensPlus = PLUS.filter((l) => !l.rolesRequis || l.rolesRequis.includes(role ?? ''));
  if (profilCompte()) liensPlus.push({ page: 'VALIDATION', label: 'Mes saisies envoyées', aide: 'Suivi de la validation par le responsable' });
  const navVisible = NAV.filter((n) => !n.modules || n.modules.some(moduleAutorise));

  async function deconnecter() {
    // Poste partagé : les données de santé ne doivent pas rester lisibles après la déconnexion… sans jamais perdre une saisie non envoyée.
    const r = await effacerDonneesLocales(false);
    if (!r.ok) {
      const ok = confirm(`${r.enAttente} saisie(s) n'ont pas encore été envoyées au serveur (pas de réseau ?).\n\nSe déconnecter EFFACERA ces saisies de cet appareil.\n\nOK = se déconnecter quand même · Annuler = rester connecté`);
      if (!ok) return;
      await effacerDonneesLocales(true);
    }
    try { for (const n of await caches.keys()) if (n.endsWith('-api')) await caches.delete(n); } catch { /* cache indisponible */ }
    try { await effacerAbonnementLocal(); } catch { /* base locale indisponible */ }
    oublierPeriodes(); localStorage.removeItem(DERNIERE);
    clearSession(); setConnecte(false); setPeriode(null); setPage('ACCUEIL');
  }

  const versAbonnement = () => setPage('ABONNEMENT');
  const enPlus = liensPlus.some((l) => l.page === page) || page === 'PLUS';

  return (
    <AbonnementProvider>
    <div className="app-shell">
      <header className="app-header">
        <img src="/icons/icon-192.png" alt="Logo" width={34} height={34} style={{ borderRadius: 8, flexShrink: 0 }} />
        <div className="entete-titre">
          <strong>{NOM_APPLICATION}</strong>
          {nomCompte && <small className="nom-compte" title="Compte connecté">{nomCompte}</small>}
        </div>
        {periode && <button className="chip-periode" onClick={() => setPage('ACCUEIL')} title="Changer de mois"><Icone nom="cal" taille="1.1em" /> {nomMois(periode.mois)} {periode.annee}</button>}
        <div className="entete-droite">
          <SyncBadge onClick={() => setPage('SYNCHRO')} />
          <button className="secondaire bouton-sortie" onClick={deconnecter}>Déconnexion</button>
        </div>
      </header>
      <div className="app-corps">
      <nav className="app-nav" aria-label="Navigation principale">
        {navVisible.map((n) => (
          <button key={n.page} className={`nav-item ${n.pages.includes(page) ? 'actif' : ''}`} aria-label={n.label} aria-current={n.pages.includes(page) ? 'page' : undefined} onClick={() => aller(n.page)}>
            <span className="nav-icone" aria-hidden="true"><Icone nom={n.icone} /></span>
            <span className="nav-texte nav-long">{n.label}</span>
            <span className="nav-texte nav-court">{n.court}</span>
          </button>
        ))}
        <button className={`nav-item nav-plus ${enPlus ? 'actif' : ''}`} aria-label="Plus" aria-current={enPlus ? 'page' : undefined} onClick={() => setPage('PLUS')}>
          <span className="nav-icone" aria-hidden="true"><Icone nom="plus" /></span>
          <span className="nav-texte nav-long">Plus</span>
          <span className="nav-texte nav-court">Plus</span>
        </button>
      </nav>
      <div className="app-colonne">

      {sessionExpiree && (
        <div className="messages-erreur" role="alert" style={{ padding: '10px 14px', margin: '8px 0' }}>
          ⚠ <strong>Session expirée.</strong> Vos saisies sont conservées sur cet appareil mais ne peuvent plus être envoyées.
          Reconnectez-vous (n'utilisez pas « Déconnexion » : elle efface les saisies non envoyées).{' '}
          <button type="button" onClick={() => { identiteAvant.current = identite(); setReconnexion(true); }}>Se reconnecter</button>
        </div>
      )}
      {reconnexion && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: '#fff', overflow: 'auto' }}>
          <Login onLoggedIn={() => {
            if (identite() !== identiteAvant.current) {
              // Autre compte que celui dont la session a expiré : on repart d'un contexte propre (période mémorisée, caches). La file locale est
              // conservée et protégée par la garde de traiterFile : elle ne part jamais sous le jeton d'un autre compte.
              localStorage.removeItem(DERNIERE); oublierPeriodes();
              window.location.reload();
              return;
            }
            setMdpAChanger(localStorage.getItem('prerma_mdp_a_changer') === '1');
            setReconnexion(false); setSessionExpiree(false); majPermissions((n) => n + 1);
            void (async () => { await chargerPermissions(); await traiterFile(); await synchroniser(); })();
          }} />
          <div className="app-content"><button type="button" className="secondaire" onClick={() => setReconnexion(false)}>Annuler</button></div>
        </div>
      )}

      <BandeauAbonnement onAbonnement={versAbonnement} />

      <main className={`app-content ${['SAISIE', 'PREINV', 'INV', 'TABLEAUX', 'COMMANDES', 'RAPPORTS'].includes(page) ? 'large' : ''}`}>
        {page === 'ACCUEIL' && <Accueil periode={periode} onPeriodeChange={setPeriode} onAller={(p) => aller(p as Page)} />}
        {page === 'SAISIE' && <Saisie periode={periode} onAbonnement={versAbonnement} />}
        {(page === 'PREINV' || page === 'INV') && (
          <>
            <Onglets<'PREINV' | 'INV'> actif={page} onChoix={setPage} onglets={[{ id: 'PREINV', label: 'Pré-inventaire' }, { id: 'INV', label: 'Inventaire' }]} />
            {page === 'PREINV'
              ? <PorteModule module="PREINVENTAIRE" onAbonnement={versAbonnement}><Preinventaire periode={periode} /></PorteModule>
              : <PorteModule module="INVENTAIRE" onAbonnement={versAbonnement}><Inventaire periode={periode} /></PorteModule>}
          </>
        )}
        {page === 'RAPPORTS' && <RapportsHebdo />}
        {page === 'TABLEAUX' && (
          <>
            <Onglets<'RMA' | 'SUIVI' | 'EXPORT'> actif={sousTableaux} onChoix={setSousTableaux}
              onglets={[{ id: 'RMA', label: 'Tableaux RMA' }, { id: 'SUIVI', label: 'Tableau de suivi' }, { id: 'EXPORT', label: 'Exports' }]} />
            {sousTableaux === 'RMA' && <Rma periode={periode} onPeriodeChange={setPeriode} />}
            {sousTableaux === 'SUIVI' && <TableauSuivi periode={periode} />}
            {sousTableaux === 'EXPORT' && <Export periode={periode} />}
          </>
        )}
        {page === 'COMMANDES' && <Commandes />}
        {page === 'PLUS' && <Plus liens={liensPlus} onAller={(p) => aller(p as Page)} />}
        {page === 'HISTORIQUE' && <Historique onOuvrir={(p) => { setPeriode(p); setSousTableaux('RMA'); setPage('TABLEAUX'); }} />}
        {page === 'ABONNEMENT' && <Abonnement />}
        {page === 'PAIEMENTS' && <PaiementsAdmin />}
        {page === 'ADMIN_COMPTES' && <AdminComptes />}
        {page === 'VALIDATION' && <Validation />}
        {page === 'ADMIN_ACCES' && <AdminAcces />}
        {page === 'ADMIN_ABONNEMENTS' && <AdminAbonnements />}
        {page === 'REFERENTIELS' && <Referentiels />}
        {page === 'PARAMETRES' && <Parametres />}
        {page === 'SYNCHRO' && <Synchronisation periode={periode} />}
      </main>
      </div>
      </div>
    </div>
    </AbonnementProvider>
  );
}
